import React from 'react';
import { motion } from 'framer-motion';
import { FileX2, TrendingUp, Wifi, Lock, Check, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';

const cambios = [
  {
    icon: FileX2,
    hoy: 'El papel y el Excel todavía se aceptan si son fiables',
    nuevo: 'Solo valdrán sistemas 100% digitales: papel y Excel, prohibidos',
  },
  {
    icon: TrendingUp,
    hoy: 'Multas de 751 a 7.500 € por empresa o centro',
    nuevo: 'Multas de 1.000 a 10.000 € por cada trabajador afectado',
  },
  {
    icon: Wifi,
    hoy: 'Los registros deben estar "a disposición" de la Inspección',
    nuevo: 'La Inspección de Trabajo tendrá acceso remoto e inmediato a tus fichajes',
  },
  {
    icon: Lock,
    hoy: 'Basta con que el registro sea objetivo y accesible',
    nuevo: 'Registros inalterables y trazables: cada cambio, documentado y validado',
  },
];

const cumplimos = [
  'Fichaje 100% digital en tablet, ordenador o móvil: sin papel, sin Excel',
  'Registros inalterables con auditoría completa de cambios',
  'Cada corrección manual la valida el propio empleado',
  'Autocierre inteligente con doble aceptación (empleado + admin)',
  'Informes oficiales exportables al instante ante una inspección',
  'Envío automático de informes a tu gestoría (semanal/mensual)',
  'Nocturnidad y descansos legales calculados automáticamente',
  'Conservación segura durante los 4 años que exige la ley',
];

export const Normativa2026 = ({ onDemo }) => (
  <section className="py-20 sm:py-28 bg-bone" data-testid="normativa-2026-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="max-w-3xl mb-14">
        <p className="font-body-landing text-terracotta font-bold text-sm tracking-[0.15em] uppercase mb-4">Nueva normativa 2026</p>
        <h2 className="font-display font-black text-forest-900 text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-5">
          El papel y el Excel tienen los días contados
        </h2>
        <p className="font-body-landing text-forest-700 text-base md:text-lg leading-relaxed">
          El Gobierno tramita un nuevo Real Decreto que endurece el registro de jornada: <strong>fichaje digital
          obligatorio</strong>, multas <strong>por trabajador</strong> (no por empresa) y <strong>acceso remoto</strong> de
          la Inspección de Trabajo a tus registros. Se espera su entrada en vigor a lo largo de 2026.
          Quien espere al último día, llegará tarde.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 rounded-3xl border border-forest-900/10 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden">
          <div className="grid grid-cols-[auto_1fr_1fr] items-center border-b border-forest-900/10 bg-forest-100/60">
            <span className="w-14" />
            <p className="font-body-landing font-bold text-forest-700/70 text-xs sm:text-sm uppercase tracking-wider py-4 pr-4">Hoy · RDL 8/2019</p>
            <p className="font-body-landing font-bold text-terracotta text-xs sm:text-sm uppercase tracking-wider py-4 pr-6">Nueva normativa · 2026</p>
          </div>
          {cambios.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              className={`grid grid-cols-[auto_1fr_1fr] items-center ${i < cambios.length - 1 ? 'border-b border-forest-900/5' : ''}`}
              data-testid={`normativa-row-${i}`}
            >
              <span className="w-14 flex justify-center"><c.icon className="w-5 h-5 text-forest-700/50" /></span>
              <p className="font-body-landing text-sm text-forest-700/70 py-5 pr-4 leading-relaxed">{c.hoy}</p>
              <p className="font-body-landing text-sm font-bold text-forest-900 py-5 pr-6 leading-relaxed">{c.nuevo}</p>
            </motion.div>
          ))}
          <div className="bg-terracotta/10 border-t border-terracotta/20 px-6 sm:px-8 py-5">
            <p className="font-body-landing text-sm text-forest-900 leading-relaxed">
              <strong className="font-display">Haz cuentas:</strong> con el nuevo régimen sancionador, una empresa de 20 empleados
              sin registro digital podría enfrentarse a multas de hasta <strong className="text-terracotta">200.000 €</strong> en una sola inspección.
            </p>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.55, delay: 0.15 }}
          className="rounded-3xl bg-forest-900 noise-overlay p-8 flex flex-col"
          data-testid="normativa-cumplimos-card"
        >
          <p className="font-display font-bold text-bone text-xl mb-2">GestionaEmpleados ya cumple lo que exigirá la nueva norma</p>
          <p className="font-body-landing text-bone/60 text-sm mb-6">Adelántate hoy y olvídate del cambio de normativa.</p>
          <ul className="space-y-3.5 mb-8">
            {cumplimos.map((f) => (
              <li key={f} className="flex gap-3 font-body-landing text-sm text-bone/85">
                <Check className="w-4 h-4 text-terracotta shrink-0 mt-0.5" />
                {f}
              </li>
            ))}
          </ul>
          <div className="mt-auto space-y-3">
            <Button asChild className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover text-white font-body-landing font-bold h-12 group">
              <Link to="/registro" data-testid="normativa-trial-btn">
                Adelántate a la norma · Gratis 15 días
                <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
            <Button variant="outline" onClick={onDemo} className="w-full rounded-full border-bone/25 text-bone hover:bg-bone/10 hover:text-bone font-body-landing font-bold h-12 bg-transparent" data-testid="normativa-demo-btn">
              Ver demo · Te llamamos
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);
